import { useId, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'

import { AGENT_ACTIVITY_COPY } from '../../consts/agent-activity'
import { AgentActivityTrail, AgentStatusStep } from '../../core/types/agentStatus.types'
import ChevronRightIcon from '../icons/chevron-right-icon'
import { customScrollbarStyles } from '../ui/custom-scrollbar.styles'
import UserAvatar from './user-avatar'

type AgentActivityBubbleProps = {
  trail: AgentActivityTrail
  /** Active placement: in the typing slot, beside the assistant avatar. */
  showAvatar?: boolean
  className?: string
}

const FG_TERTIARY = 'var(--ds-fg-tertiary, #8c92a5)'
const FG_SECONDARY = 'var(--ds-fg-secondary, #647089)'
const SHIMMER_HIGHLIGHT = 'var(--ds-shimmer-highlight, oklch(27.603% 0.02235 277.43))'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    gap: 12,
  },
  column: {
    display: 'flex',
    minWidth: 0,
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  row: {
    display: 'flex',
    maxWidth: '100%',
    alignItems: 'center',
  },
  rowWithAvatar: {
    minHeight: 40,
  },
  // A highlight band travels left to right across grey text (D-22, D-26).
  shimmer: {
    color: FG_TERTIARY,
    backgroundImage: [
      'linear-gradient(100deg',
      `${FG_TERTIARY} 0%`,
      `${FG_TERTIARY} 38%`,
      `${SHIMMER_HIGHLIGHT} 50%`,
      `${FG_TERTIARY} 62%`,
      `${FG_TERTIARY} 100%)`,
    ].join(', '),
    backgroundSize: '250% 100%',
    backgroundPosition: '100% 0',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: '$shimmer 2.2s linear infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      backgroundImage: 'none',
      WebkitTextFillColor: 'currentColor',
    },
  },
  '@keyframes shimmer': {
    from: {
      backgroundPosition: '100% 0',
    },
    to: {
      backgroundPosition: '0% 0',
    },
  },
  label: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 14,
    lineHeight: '20px',
  },
  toggle: {
    display: 'inline-flex',
    maxWidth: '100%',
    alignItems: 'center',
    gap: 10,
    padding: 0,
    border: 0,
    borderRadius: 2,
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    font: 'inherit',
    '&:focus-visible': {
      outline: '2px solid var(--ds-border-focus, #f47d6b)',
      outlineOffset: 2,
    },
    '&:hover $count': {
      color: FG_SECONDARY,
    },
  },
  count: {
    display: 'inline-flex',
    flexShrink: 0,
    alignItems: 'center',
    gap: 4,
    color: FG_TERTIARY,
    transition: 'color 150ms ease',
  },
  countActive: {
    fontSize: 12,
  },
  countClosed: {
    fontSize: 13,
    lineHeight: '20px',
  },
  chevron: {
    width: 12,
    height: 12,
    flexShrink: 0,
    '@media (prefers-reduced-motion: no-preference)': {
      transition: 'transform 150ms ease',
    },
  },
  chevronExpanded: {
    transform: 'rotate(90deg)',
  },
  discarded: {
    margin: '8px 0 0 6px',
    paddingLeft: 16,
    fontSize: 12,
    color: FG_TERTIARY,
  },
  // At most 8 rows, then it scrolls: 8 × 20px + 7 × 8px gap = 216px (D-27).
  list: {
    margin: '8px 0 0 6px',
    display: 'flex',
    maxHeight: 216,
    width: '100%',
    flexDirection: 'column',
    gap: 8,
    overflowY: 'auto',
    borderLeft: '1px solid var(--ds-border-default, #d7dae4)',
    padding: '0 4px 0 16px',
    listStyle: 'none',
    boxSizing: 'border-box',
    ...customScrollbarStyles,
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
    lineHeight: '20px',
    color: FG_TERTIARY,
  },
  dot: {
    width: 5,
    height: 5,
    flexShrink: 0,
    borderRadius: '50%',
    background: 'var(--ds-fg-disabled, #9aa1b4)',
  },
  dotCurrent: {
    background: FG_TERTIARY,
  },
  stepLabel: {
    minWidth: 0,
  },
}))

/**
 * The reasoning line (v3, alternative A): the agent's current step as grey text with a
 * left-to-right shimmer while it works, and a "Reasoned in N steps" line above the answer once
 * it is done. The text itself is the disclosure control for the full trail (D-25).
 */
const AgentActivityBubble = ({
  trail,
  showAvatar = false,
  className,
}: AgentActivityBubbleProps) => {
  const classes = useStyles()
  const [isExpanded, setIsExpanded] = useState(false)
  const listId = `agent-activity-${useId()}`

  const { steps } = trail
  if (steps.length === 0) return null

  const isActive = trail.status === 'active'
  const currentStep = steps[steps.length - 1] ?? null
  const stepCount = steps.length
  const droppedSteps = trail.droppedSteps
  const canExpand = stepCount > 1 || !isActive
  const activeCountLabel =
    stepCount === 1 ? AGENT_ACTIVITY_COPY.stepsOne : AGENT_ACTIVITY_COPY.stepsMany(stepCount)
  const closedLabel =
    stepCount === 1 ? AGENT_ACTIVITY_COPY.reasonedOne : AGENT_ACTIVITY_COPY.reasonedMany(stepCount)
  const discardedLabel =
    droppedSteps === 1
      ? AGENT_ACTIVITY_COPY.discardedOne
      : AGENT_ACTIVITY_COPY.discardedMany(droppedSteps)
  const isCurrentStep = (step: AgentStatusStep): boolean =>
    isActive && step.id === currentStep?.id

  return (
    <div className={`${classes.root} ${className ?? ''}`}>
      {showAvatar ? <UserAvatar type="assistant" /> : null}

      <div className={classes.column}>
        <div className={`${classes.row} ${showAvatar ? classes.rowWithAvatar : ''}`}>
          {!canExpand ? (
            <span className={`${classes.label} ${classes.shimmer}`} aria-live="polite">
              {currentStep?.label}
            </span>
          ) : (
            <button
              type="button"
              className={classes.toggle}
              aria-expanded={isExpanded}
              aria-controls={listId}
              onClick={() => setIsExpanded((value) => !value)}
            >
              {isActive ? (
                <span className={`${classes.label} ${classes.shimmer}`} aria-live="polite">
                  {currentStep?.label}
                </span>
              ) : null}
              <span
                className={`${classes.count} ${
                  isActive ? classes.countActive : classes.countClosed
                }`}
              >
                {isActive ? activeCountLabel : closedLabel}
                <ChevronRightIcon
                  className={`${classes.chevron} ${isExpanded ? classes.chevronExpanded : ''}`}
                />
              </span>
            </button>
          )}
        </div>

        {isExpanded ? (
          <>
            {droppedSteps > 0 ? <p className={classes.discarded}>{discardedLabel}</p> : null}

            <ol id={listId} className={classes.list} aria-label="Reasoning steps">
              {steps.map((step) => (
                <li key={step.id} className={classes.step}>
                  <span
                    aria-hidden="true"
                    className={`${classes.dot} ${isCurrentStep(step) ? classes.dotCurrent : ''}`}
                  />
                  <span
                    className={`${classes.stepLabel} ${isCurrentStep(step) ? classes.shimmer : ''}`}
                  >
                    {step.label}
                  </span>
                </li>
              ))}
            </ol>
          </>
        ) : null}
      </div>
    </div>
  )
}

export default AgentActivityBubble
